import React, { useState, useEffect, useCallback } from 'react';

import { useWindowFocus } from '@src/hooks/useWindowFocus';

/* Api */
import { useMutationCreateCreator } from '@src/queries/influencers/useMutationCreateCreator';

/* Styling */
import { stylesheet } from 'typestyle';

/* Misc */
import { EPopup } from '@src/types/common';
import { setPopup } from '@src/state/popupAtoms';
import { useQueryUserConfig } from '../../../../../queries/users/useQueryUserConfig';
import { LocalizedStrings } from '@src/utils/localizedStrings';

/* Components */
import { PopupOverlay } from '@src/components/PopupOverlay';
import { Input } from 'components/Input';
import { ActionButton } from '@src/components/ActionButton';
import { SectionProgressLoader } from '@src/components/SectionProgressLoader';

/* Types */
import { Services } from '@src/types/Services';
import { normalizeCreatorUsername } from '@src/utils/creatorUsername';

interface Props {
  services: Services;
}

export function Blacklist(props: Props) {
  const { data: userConfig } = useQueryUserConfig();
  const user = userConfig?.user ?? null;
  const focus = useWindowFocus();
  const [mounted, setMounted] = useState(false);
  const [influencerData, setInfluencerData] = useState({ name: '',
    localizations: [] as string[],
    blacklisted: true });

  const { mutateAsync: mutateInfluencer, isPending: saving } = useMutationCreateCreator();
  const normalizedUsername = normalizeCreatorUsername(influencerData.name);

  const onSaveClick = async () => {
    await mutateInfluencer({
      username: normalizedUsername,
      isBlacklisted: true,
      upsert: true,
    });
    close();
  };

  const refresh = (): void | NodeJS.Timeout => {
    if (!mounted) {
      return;
    }
    const loop = () => setTimeout(() => refresh(), 3000);
    if (!focus) {
      return loop();
    }
  };

  const close = useCallback(() => {
    setPopup(EPopup.addBlacklist, false);
  }, []);

  useEffect(() => {
    setMounted(true);
    refresh();
    return () => {
      setMounted(false);
    };
  }, []);

  const disableSubmit = normalizedUsername.length < 1 || normalizedUsername.length > 30;

  if (!user) {
    return null; // TODO: loading
  }
  return (
    <>
      <PopupOverlay show={true} onClose={close} title={lang.title} width="min(500px, 92vw)" zIndex={3000}>

        <Input
          onChange={({ target: { value } }) =>
            setInfluencerData({ ...influencerData, name: value })
          }
          value={influencerData.name}
          placeholder={lang.name}
        />

        <div className={sheet.buttonContainer}>
          <ActionButton
            variant='blue'
            onAction={onSaveClick}
            disabled={disableSubmit}
            data-size='medium'
          >
            {lang.save.toUpperCase()}
          </ActionButton>
        </div>

        {saving ? <SectionProgressLoader indeterminate opacity={0.6} /> : null}
      </PopupOverlay>
    </>
  );
}

const lang = LocalizedStrings({
  en: {
    title: 'Blacklist a creator',
    name: 'Enter an instagram name',
    error: 'An error occured, please try later or contact us',
    save: 'Add to blacklist',
  },
});

const sheet = stylesheet({
  buttonContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: '20px',
  },
  text: {
    margin: '20px 0',
  },
});
export default Blacklist;
